import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useValueChurnProbe } from "../utils/loopDebug";
import { Product } from "../types/products";
import { API_CONFIG, getApiUrl } from "../config/api";
import { useSalon } from "./SalonContext";
import { useApp } from "./AppContext";
import usePermissions from "../hooks/usePermissions";

interface ProductsContextType {
  products: Product[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  error: string | null;
  searchProducts: (
    query: string,
    page: number,
    limit: number,
  ) => Promise<Product[]>;
  getProductById: (id: string) => Product | undefined;
  getProductByCustomId: (customId: string) => Product | undefined;
  fetchProducts: () => Promise<void>;
  addProduct: (
    productData: Omit<
      Product,
      "id" | "created_dt" | "updated_dt" | "created_by" | "updated_by"
    >,
  ) => Promise<Product[]>;
  updateProduct: (
    productId: string,
    updates: Partial<Product>,
  ) => Promise<Product[]>;
  getLowStockProducts: () => Product[];
}

const ProductsContext = createContext<ProductsContextType | null>(null);

export function ProductsProvider({ children }: { children: React.ReactNode }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { currentUser, businessInfo } = useSalon() as any;
  const { fetchWithAuth } = useApp();
  const perms = usePermissions();
  const productsPerms = perms?.products;
  const canViewProducts =
    productsPerms != null
      ? productsPerms.canView !== false
      : currentUser?.role === "Owner" || currentUser?.role === "Manager";

  const fetchProducts = useCallback(async () => {
    if (!businessInfo?.id || !canViewProducts) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_PRODUCTS, "", {
          salonId: businessInfo?.id,
        }),
      );
      setProducts(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch products");
      console.error("Error fetching products:", err);
    } finally {
      setIsLoading(false);
    }
  }, [businessInfo?.id, canViewProducts]);

  const addProduct = useCallback(
    async (
      productData: Omit<
        Product,
        "id" | "created_dt" | "updated_dt" | "created_by" | "updated_by"
      >,
    ) => {
      if (!businessInfo?.id) {
        throw new Error("No salon ID available");
      }

      try {
        setIsLoading(true);
        setError(null);

        const response: Product[] = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.PRODUCTS),
          {
            method: "POST",
            body: JSON.stringify({
              salon_id: businessInfo.id,
              products: [productData],
            }),
          },
        );

        setProducts((prev: Product[]) => [...prev, ...response]);
        return response;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to add product");
        console.error("Error adding product:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [businessInfo?.id],
  );

  const updateProduct = useCallback(
    async (productId: string, updates: Partial<Product>) => {
      if (!businessInfo?.id) {
        throw new Error("No salon ID available");
      }

      try {
        setIsLoading(true);
        setError(null);

        const response: Product[] = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.PRODUCTS),
          {
            method: "PUT",
            body: JSON.stringify({
              salon_id: businessInfo.id,
              products: [
                {
                  id: productId,
                  ...updates,
                },
              ],
            }),
          },
        );

        setProducts((prev: Product[]) => {
          const updatedProduct = response.find(
            (r: Product) => r.id === productId,
          );
          if (updatedProduct) {
            return prev.map((p: Product) =>
              p.id === productId ? { ...p, ...updatedProduct } : p,
            );
          }
          return prev;
        });
        return response;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to update product",
        );
        console.error("Error updating product:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [businessInfo?.id],
  );

  const searchProducts = useCallback(
    async (query: string, page: number, limit: number): Promise<Product[]> => {
      // Simulate API delay for search
      await new Promise((resolve) => setTimeout(resolve, 300));

      let filteredProducts = products;

      // Apply status filter
      if (statusFilter !== "All") {
        filteredProducts = filteredProducts.filter(
          (product: Product) => product.status === statusFilter,
        );
      }

      // Apply search query
      if (query.trim()) {
        const lowerQuery = query.toLowerCase();
        filteredProducts = filteredProducts.filter(
          (product: Product) =>
            product.custom_id?.toLowerCase().includes(lowerQuery) ||
            product.name?.toLowerCase().includes(lowerQuery) ||
            product.company?.toLowerCase().includes(lowerQuery) ||
            product.category?.toLowerCase().includes(lowerQuery),
        );
      }

      // Apply pagination
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      return filteredProducts.slice(startIndex, endIndex);
    },
    [products, statusFilter],
  );

  const getProductById = useCallback(
    (id: string): Product | undefined => {
      return products.find((product: Product) => product.id === id);
    },
    [products],
  );

  const getProductByCustomId = useCallback(
    (customId: string): Product | undefined => {
      return products.find(
        (product: Product) => product.custom_id === customId,
      );
    },
    [products],
  );

  const getLowStockProducts = useCallback((): Product[] => {
    return products.filter(
      (product: Product) =>
        product.status === "Active" &&
        product.quantity <= product.reorder_threshold,
    );
  }, [products]);

  // Fetch products when salon or view permission changes
  useEffect(() => {
    if (businessInfo?.id && canViewProducts) {
      fetchProducts();
    }
  }, [businessInfo?.id, canViewProducts, fetchProducts]);

  // [LOOP] churn probe
  useValueChurnProbe("ProductsContext", { products, isLoading, error });

  // Memoized so consumers don't re-render on every provider render; every
  // method above is useCallback'd or a state setter, so deps are stable.
  const value: ProductsContextType = useMemo(
    () => ({
      products,
      searchQuery,
      setSearchQuery,
      statusFilter,
      setStatusFilter,
      currentPage,
      setCurrentPage,
      pageSize,
      setPageSize,
      isLoading,
      error,
      searchProducts,
      getProductById,
      getProductByCustomId,
      fetchProducts,
      addProduct,
      updateProduct,
      getLowStockProducts,
    }),
    [
      products,
      searchQuery,
      statusFilter,
      currentPage,
      pageSize,
      isLoading,
      error,
      searchProducts,
      getProductById,
      getProductByCustomId,
      fetchProducts,
      addProduct,
      updateProduct,
      getLowStockProducts,
    ],
  );

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
}

export function useProducts() {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error("useProducts must be used within a ProductsProvider");
  }
  return context;
}
